// The v2 board's CSV columns (GRO-276): one per table column, in the table's order, with the
// full value rather than the cell's short form. Plain .ts so node --test can load it; the
// table's columns (boardV2Columns.tsx) take their key, label and accessor from here.

import { relationshipPill, verticalReading, whyNowCell } from './boardCells.ts';
import type { RankedAccount } from './derived.ts';
import { lastOutbound } from './derived.ts';
import { fundingCsvValue, relationshipCsvValue } from './funding.ts';
import { bucketCsvValue, engagementCsvValue, fitCsvValue, scoreRankOf } from './scores.ts';

export interface BoardV2CsvColumn {
  key: string;
  label: string;
  accessor: (row: RankedAccount) => string;
}

// The weekly pass's line and detail, else the best_signal fact in full, else Scout's ranking
// label and reason.
function whyNowCsv(row: RankedAccount): string {
  const reading = whyNowCell(row);
  if (reading?.pass) return `${reading.pass.line} — ${reading.pass.detail}`;
  if (reading?.signal) return reading.signal.fact;
  const reason = row.work_reason;
  return reason ? [reason.label, reason.detail].filter(Boolean).join(' — ') : '';
}

// The three score columns (GRO-381), right after #. Present only when scores are visible.
const SCORE_CSV_COLUMNS: BoardV2CsvColumn[] = [
  { key: 'fit_score', label: 'Fit', accessor: fitCsvValue },
  { key: 'engagement_score', label: 'Engmt', accessor: engagementCsvValue },
  { key: 'bucket', label: 'Bucket', accessor: bucketCsvValue },
];

export function boardV2CsvColumns(includeScores = false): BoardV2CsvColumn[] {
  const columns: BoardV2CsvColumn[] = [
    { key: 'rank', label: '#', accessor: row => (row.ranking == null ? '' : String(row.ranking)) },
    { key: 'account', label: 'Account', accessor: row => row.account },
    { key: 'why_now', label: 'Why now', accessor: whyNowCsv },
    { key: 'vertical', label: 'Vertical', accessor: row => verticalReading(row)?.title ?? '' },
    { key: 'relationship_pill', label: 'Relationship', accessor: row => relationshipPill(row).label },
    { key: 'account_owner', label: 'AE', accessor: row => row.account_owner ?? '' },
    { key: 'last_out', label: 'Last touch', accessor: row => lastOutbound(row)?.day ?? '' },
    { key: 'credits_30', label: 'Usage 30d', accessor: row => (row.credits_30 == null ? '' : String(row.credits_30)) },
    // The v1 CSV's own readers: "None of our funds" for none, blank for unknown, "; " joins.
    { key: 'funding', label: 'Funding', accessor: fundingCsvValue },
    { key: 'relationship', label: 'Our VC', accessor: relationshipCsvValue },
  ];
  if (!includeScores) return columns;

  // With scores, # is the score-order rank (2 x Fit + Engagement) and Fit, Engmt, Bucket follow it.
  const [rankColumn, ...otherColumns] = columns;
  const scoreRankColumn = {
    ...rankColumn!,
    accessor: (row: RankedAccount) => String(scoreRankOf(row) ?? ''),
  };
  return [scoreRankColumn, ...SCORE_CSV_COLUMNS, ...otherColumns];
}
